import path from "node:path";

import {
  PdfEngineError,
  type NormalizedRenderPdfOptions,
  type PdfMargins,
  type RenderPdfOptions
} from "../types/types";

const DEFAULT_TIMEOUT = 30_000;
const DEFAULT_SCALE = 1;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function assertCssLength(value: unknown, fieldName: string): asserts value is string {
  if (!isNonEmptyString(value)) {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      `${fieldName} must be a non-empty CSS length string.`
    );
  }
}

function assertSafePath(filePath: string, fieldName: string): void {
  if (filePath.includes("\0")) {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      `${fieldName} contains an invalid null byte.`
    );
  }
}

function normalizeMargins(margins: PdfMargins | undefined): Required<PdfMargins> | undefined {
  if (!margins) {
    return undefined;
  }

  if (margins.top !== undefined) {
    assertCssLength(margins.top, "margins.top");
  }

  if (margins.right !== undefined) {
    assertCssLength(margins.right, "margins.right");
  }

  if (margins.bottom !== undefined) {
    assertCssLength(margins.bottom, "margins.bottom");
  }

  if (margins.left !== undefined) {
    assertCssLength(margins.left, "margins.left");
  }

  return {
    top: margins.top ?? "0",
    right: margins.right ?? "0",
    bottom: margins.bottom ?? "0",
    left: margins.left ?? "0"
  };
}

export function validateOptions(options: RenderPdfOptions): NormalizedRenderPdfOptions {
  if (!options || typeof options !== "object") {
    throw new PdfEngineError("VALIDATION_ERROR", "renderPdf options are required.");
  }

  if (!isNonEmptyString(options.html)) {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      "html must be a non-empty string."
    );
  }

  if (!options.size || typeof options.size !== "object") {
    throw new PdfEngineError("VALIDATION_ERROR", "size is required.");
  }

  assertCssLength(options.size.width, "size.width");
  assertCssLength(options.size.height, "size.height");

  const output = options.output ?? "buffer";
  if (output !== "buffer" && output !== "file") {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      'output must be either "buffer" or "file".'
    );
  }

  const normalized: NormalizedRenderPdfOptions = {
    html: options.html,
    size: {
      width: options.size.width,
      height: options.size.height
    },
    output,
    scale: options.scale ?? DEFAULT_SCALE,
    timeout: options.timeout ?? DEFAULT_TIMEOUT
  };

  const margins = normalizeMargins(options.margins);
  if (margins) {
    normalized.margins = margins;
  }

  if (options.landscape !== undefined) {
    if (typeof options.landscape !== "boolean") {
      throw new PdfEngineError(
        "VALIDATION_ERROR",
        "landscape must be a boolean when provided."
      );
    }

    normalized.landscape = options.landscape;
  }

  if (!Number.isFinite(normalized.scale) || normalized.scale < 0.1 || normalized.scale > 2) {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      "scale must be a number between 0.1 and 2."
    );
  }

  if (
    !Number.isFinite(normalized.timeout) ||
    normalized.timeout <= 0 ||
    !Number.isInteger(normalized.timeout)
  ) {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      "timeout must be a positive integer in milliseconds."
    );
  }

  if (options.executablePath !== undefined) {
    assertCssLength(options.executablePath, "executablePath");
    assertSafePath(options.executablePath, "executablePath");
    normalized.executablePath = options.executablePath;
  }

  if (output === "file") {
    if (!isNonEmptyString(options.outputPath)) {
      throw new PdfEngineError(
        "VALIDATION_ERROR",
        'outputPath is required when output is "file".'
      );
    }

    assertSafePath(options.outputPath, "outputPath");
    normalized.outputPath = path.resolve(options.outputPath);
  }

  return normalized;
}
